import { hapticManager } from './haptic';
import { mascotManager } from './mascot';

type Tone = 'correct' | 'wrong' | 'click' | 'win';

export class AudioManager {
  private ctx: AudioContext | null = null;
  private muted = false;

  private ensureCtx(): AudioContext | null {
    if (this.muted) return null;
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  setMuted(value: boolean): void {
    this.muted = value;
    if (value && this.ctx) {
      void this.ctx.suspend();
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  play(tone: Tone): void {
    // Haptic + Mascote acoplados: disparam automaticamente quando o áudio toca.
    // Assim as 35 atividades ganham vibração + reação visual sem mexer em cada uma.
    if (tone === 'correct') {
      hapticManager.vibrate('success');
      mascotManager.react('correct');
    } else if (tone === 'wrong') {
      hapticManager.vibrate('error');
      mascotManager.react('wrong');
    } else if (tone === 'click') {
      hapticManager.vibrate('tap');
    } else if (tone === 'win') {
      hapticManager.vibrate('celebrate');
      mascotManager.react('win');
    }

    const ctx = this.ensureCtx();
    if (!ctx) return;

    if (tone === 'correct') {
      this.chordC();
      return;
    }
    if (tone === 'win') {
      this.celebrate();
      return;
    }

    const presets: Record<'wrong' | 'click', { freq: number; freqEnd: number; duration: number; type: OscillatorType }> = {
      wrong:   { freq: 220, freqEnd: 160, duration: 0.22, type: 'square' },
      click:   { freq: 800, freqEnd: 800, duration: 0.04, type: 'triangle' },
    };

    const p = presets[tone as 'wrong' | 'click'];
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = p.type;
    osc.frequency.setValueAtTime(p.freq, t0);
    osc.frequency.exponentialRampToValueAtTime(p.freqEnd, t0 + p.duration);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + p.duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + p.duration + 0.02);
  }

  // Acorde maior (Dó-Mi-Sol) usado pra 'correct' — mais rico que o sine único anterior.
  private chordC(): void {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const freqs = [523.25, 659.25, 783.99];
    const duration = 0.3;
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t0);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.13, t0 + 0.01 + i * 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    });
  }

  // Melodia de 4 notas pra fim de nível: Dó-Mi-Sol-Dó (uma oitava acima).
  private celebrate(): void {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.50];
    const each = 0.12;
    notes.forEach((freq, i) => {
      const start = t0 + i * each;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + each + 0.05);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + each + 0.08);
    });
  }
}

export const audioManager = new AudioManager();
